import { idSchema, referenceSchema } from "@reactive-resume/schema";
import { dateSchema } from "@reactive-resume/utils";
import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { permissionSchema } from "../permission";
import { roleSchema } from "../role";
import { secretsSchema } from "../secrets";

export const usernameSchema = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[\w.-]+$/, {
    message: "Usernames can only contain letters, numbers, periods, hyphens, and underscores.",
  })
  .transform((value) => value.toLowerCase());

export const userSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(255),
  picture: z.literal("").or(z.null()).or(z.string().url()),
  email: z
    .string()
    .email()
    .transform((value) => value.toLowerCase()),
  locale: z.string().default("en-US"),
  emailVerified: z.boolean().default(false),
  twoFactorEnabled: z.boolean().default(false),
  provider: z.enum(["email", "github", "google", "openid"]).default("email"),
  createdAt: dateSchema,
  updatedAt: dateSchema,

  /*
   * Préférence géographique et devise.
   *
   * Les champs acceptent toujours une valeur absente en entrée afin
   * de préserver la compatibilité avec les anciens objets métier.
   *
   * Après validation, ils sont normalisés vers les valeurs réellement
   * garanties par le modèle Prisma :
   *
   * - les champs nullables deviennent null lorsqu'ils sont absents ;
   * - countryNotProvided devient false lorsqu'il est absent.
   *
   * Le DTO produit ainsi exactement les mêmes types que Prisma, sans
   * imposer ces nouvelles propriétés aux anciennes données en entrée.
   */
  countryCode: z
    .string()
    .length(2)
    .nullable()
    .optional()
    .transform((value) => value ?? null),

  pricingCurrencyCode: z
    .string()
    .length(3)
    .nullable()
    .optional()
    .transform((value) => value ?? null),

  countryNotProvided: z
    .boolean()
    .optional()
    .transform((value) => value ?? false),

  countryPreferenceSetAt: dateSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),

  // Rôle "legacy" (enum 0-3) restauré pour la compatibilité client.
  // Dérivé côté serveur depuis la matrice RBAC v2 (deriveLegacyRole :
  // admin→0, manager/isInternal→1, sinon→3). Défaut = ExtenalUser (client).
  role: z.number().min(0).max(3).default(3),
  isActivated: z.boolean().default(false),
  referee: referenceSchema.optional(),
  isInternal: z.boolean().default(false),

  roles: z
    .array(roleSchema)
    .or(z.array(z.record(z.unknown())))
    .optional(),
  permission: z.array(permissionSchema).optional(),
});

export class UserDto extends createZodDto(userSchema) {}

export const userWithSecretsSchema = userSchema.merge(
  z.object({
    secrets: secretsSchema.nullable().default(null),
  }),
);

export class UserWithSecrets extends createZodDto(userWithSecretsSchema) {}